/* eslint-disable max-len */
import React from 'react';
import styled from 'styled-components';
import { withFormik } from 'formik';
import { Column } from 'rbx';
import * as yup from 'yup';
import swal from 'sweetalert';
import moment from 'moment-timezone';
import { isNaN } from 'lodash';
import { toast } from 'react-toastify';

import { FaAngleLeft } from 'react-icons/fa';
import {
  Radio,
  TextArea,
  CheckBox,
  Select,
} from '../../../components/elements';
import { updateOrderWithRefund } from '../Refunds/helpers';
import RefundStats from '../Refunds/RefundStats';

const Container = styled.form`
  &&& {
    .modal-background {
      background-color: rgba(13, 13, 13, 0.28);
    }
    .modal-card-body {
      height: 100vh;
      padding: 0px;
      overflow-y: auto;
    }
    .modal-card {
      max-height: calc(100vh - 0px);
      position: absolute;
      right: 0;
      width: 35rem;
    }
    .modal-card-head,
    .modal-card-foot {
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
    }
    .modal-card-title {
      font-size: 1.5rem;
      font-weight: 600;
    }
    .modal-card-head {
      border-bottom: 0px;
      padding: 1rem 2rem;
      padding-top: 1.5rem;
      background: white;
    }
    .modal-card-foot {
      justify-content: center;
      background: white;
      border-top: 0px;
      .button {
        font-weight: 600;
      }
    }
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  left: 1rem;
  top: 1rem;
`;

const DetailsFormWrapper = styled.div`
  padding: 1rem 2rem;
`;

const Wrapper = styled.div`
  padding-top: 0.8rem;
`;

const RadioWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 50%;
`;

const SectionTitle = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  border-top: 1px solid #eee;
  margin-top: 1rem;
`;

const LeftSpan = styled.span`
  display: flex;
  font-size: 0.9rem;
`;

const RightSpan = styled.span`
  padding-left: 10px;
  font-weight: 700;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 250px;
  display: inline-block;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
`;

const Form = ({
  isActive,
  onClose,
  handleSubmit,
  resetForm,
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  errors,
  touched,
  order,
  isSubmitting,
}) => (
  <Container className={`modal ${isActive && 'is-active'}`}>
    <div className="modal-background" />
    <div className="modal-card">
      <header className="modal-card-head">
        <p
          className="modal-card-title is-flex"
          style={{ justifyContent: 'center' }}
        >
          Refund And Cancel Order
        </p>

        <CloseButtonWrapper>
          <FaAngleLeft
            size={30}
            type="button"
            onClick={() => {
              onClose();
              resetForm();
            }}
          />
        </CloseButtonWrapper>
      </header>

      <section className="modal-card-body">
        <DetailsFormWrapper>
          <SectionTitle>Refund Details</SectionTitle>

          {order && order.billing && (
            <>
              <InfoRow>
                <LeftSpan>Order Total:</LeftSpan>
                <RightSpan>
                  ${parseFloat(order.billing.order_total || 0).toFixed(2)}
                </RightSpan>
              </InfoRow>
              <InfoRow>
                <LeftSpan>Order Amount:</LeftSpan>
                <RightSpan>
                  ${parseFloat(order.billing.order_amount || 0).toFixed(2)}
                </RightSpan>
              </InfoRow>
            </>
          )}

          <InfoRow>
            <LeftSpan>Previously Refunded:</LeftSpan>
            <RightSpan>
              ${parseFloat(values.previousRefund || 0).toFixed(2)}
            </RightSpan>
          </InfoRow>

          <Column.Group style={{ marginTop: '0.5rem' }}>
            <div className="column">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LeftSpan>
                  Refund Amount&nbsp;
                  <span className="has-text-danger">*</span>
                </LeftSpan>
                <div className="field" style={{ paddingLeft: '10px' }}>
                  <p className="control has-icons-left">
                    <input
                      className="input is-primary"
                      type="number"
                      placeholder="0.00"
                      style={{ width: '120px' }}
                      value={values.refundAmount || ''}
                      onChange={e => {
                        if (e.target.value === '') {
                          setFieldValue('refundAmount', null);
                        } else if (!isNaN(parseFloat(e.target.value))) {
                          setFieldValue(
                            'refundAmount',
                            parseFloat(e.target.value),
                          );
                        }
                      }}
                    />
                    <span className="icon is-small is-left">$</span>
                  </p>
                </div>
              </div>
            </div>
          </Column.Group>

          <Column.Group>
            <div className="column">
              <Select
                label="Refund Reason"
                options={[
                  {
                    label: 'Duplicate',
                    value: 'Duplicate',
                  },
                  {
                    label: 'Fraudulent',
                    value: 'Fraudulent',
                  },
                  {
                    label: 'Requested by customer',
                    value: 'Requested by customer',
                  },
                  {
                    label: 'Other',
                    value: 'Other',
                  },
                ]}
                value={[values.refundReason]}
                required
              />
            </div>
          </Column.Group>

          <Column.Group>
            <div className="column">
              <CheckBox
                label="Refund From Restaurant"
                name="refund_from_restaurant"
                value={values.reverse_transfer}
                onChange={() =>
                  setFieldValue('reverse_transfer', !values.reverse_transfer)
                }
                disabled={
                  values.hasAlreadyRefunded || values.refund_application_fee
                }
              />
              <CheckBox
                label="Refund Kravein Fee"
                name="refund_kravein_fee"
                value={values.refund_application_fee}
                onChange={() => {
                  setFieldValue(
                    'refund_application_fee',
                    !values.refund_application_fee,
                  );
                  setFieldValue(
                    'reverse_transfer',
                    !values.refund_application_fee,
                  );
                }}
                disabled={values.hasAlreadyRefunded}
              />
            </div>
          </Column.Group>

          <Column.Group>
            <div className="column">
              <TextArea
                label="Refund Note"
                name="refundNote"
                optionalText="(Min 10 and Max 8000 Characters)"
                placeholder="Add more details about this refund"
                value={values.refundNote || ''}
                onChange={e => setFieldValue('refundNote', e.target.value)}
                required
              />
            </div>
          </Column.Group>

          <Column.Group>
            <div className="column">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LeftSpan>
                  Approval Code&nbsp;
                  <span className="has-text-danger">*</span>
                </LeftSpan>
                <div style={{ paddingLeft: '15px' }}>
                  <input
                    className="input is-primary"
                    type="text"
                    placeholder="XXXXXXX"
                    style={{ width: '150px' }}
                    value={values.approvalCode || ''}
                    onChange={e => {
                      setFieldValue('approvalCode', e.target.value);
                    }}
                  />
                </div>
              </div>
            </div>
          </Column.Group>

          {values.refundSummary && values.refundSummary.length > 0 && (
            <>
              <SectionTitle>Previous Refunds</SectionTitle>
              <RefundStats stats={values.refundSummary} />
            </>
          )}
          <SectionTitle>Cancellation Details</SectionTitle>
          <p className="is-size-4" style={{ paddingTop: '10px' }}>
            We strongly recommend to avoid cancelling an order as it can cause a
            negative customer experience and affect your place rating. Please
            reach out to the customer to work out a solution and only cancel on
            unique situations.
          </p>
          <br />
          <>
            <div className="field">
              <div className="control">
                <RadioWrapper>
                  <Radio
                    label="Out of an item (or items)"
                    value={values.type === 'Out of an item (or items)'}
                    onChange={() =>
                      setFieldValue('type', 'Out of an item (or items)')
                    }
                  />
                </RadioWrapper>
                <div className="is-flex">
                  <RadioWrapper>
                    <Wrapper>
                      <Radio
                        label="Cant fullfill instructions"
                        value={values.type === 'Cant fullfil instructions'}
                        onChange={() =>
                          setFieldValue('type', 'Cant fullfil instructions')
                        }
                      />
                    </Wrapper>
                  </RadioWrapper>
                </div>
                <div className="is-flex">
                  <RadioWrapper>
                    <Wrapper>
                      <Radio
                        label="Problem in restaurant"
                        value={values.type === 'Problem in restaurant'}
                        onChange={() =>
                          setFieldValue('type', 'Problem in restaurant')
                        }
                      />
                    </Wrapper>
                  </RadioWrapper>
                </div>
                <div className="is-flex">
                  <RadioWrapper>
                    <Wrapper>
                      <Radio
                        label="Other"
                        value={values.type === 'Other'}
                        onChange={() => setFieldValue('type', 'Other')}
                      />
                    </Wrapper>
                  </RadioWrapper>
                </div>
              </div>
            </div>
          </>
          <br />

          <Column.Group>
            <div className="column">
              <TextArea
                name="value"
                label="Note to the customer"
                value={values.value}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                errors={errors.value}
                touched={touched.value}
                disableToastMessage
                placeholder="Add a note and mention anything specific such as no nuts or cross contamination, less spicy"
              />
              Characters left ({values.value.length}/144)
            </div>
          </Column.Group>
        </DetailsFormWrapper>
      </section>
      <footer className="modal-card-foot">
        <button
          className={`button is-large is-fullwidth is-primary ${isSubmitting &&
            'is-loading'}`}
          onClick={handleSubmit}
        >
          REFUND & CANCEL ORDER
        </button>
      </footer>
    </div>
  </Container>
);

const transformRefundAmount = val => {
  const num = parseFloat(val);
  return isNaN(num) ? 0 : Math.abs(num);
};

const extractRefundData = order => {
  const refundsDetails =
    order?.payment?.refund && Array.isArray(order?.payment?.refund)
      ? order.payment.refund
      : [];

  const refundSummary = [];
  let totalRefund = 0;
  let lastRefund = 0;
  let refundApplicationFee = false;
  let reverseTransfer = true;

  refundsDetails.forEach(ele => {
    const ra = transformRefundAmount(ele.refund_amount);
    totalRefund += ra;
    lastRefund = ra;

    refundApplicationFee = ele.refund_application_fee;
    reverseTransfer = ele.reverse_transfer;

    refundSummary.push({
      userId: ele.created_by,
      refundAmount: ra,
      refundType: ele.refund_type,
      refundedAt: ele.created_at,
      status: ele.status,
      refundReason: ele.refund_reason,
      note: ele.note,
    });
  });

  return {
    totalRefund,
    lastRefund,
    refundApplicationFee,
    reverseTransfer,
    refundSummary,
    hasAlreadyRefunded: totalRefund !== 0,
  };
};

const CancelAndRefundForm = withFormik({
  mapPropsToValues: ({ order }) => {
    const refundData = extractRefundData(order);
    return {
      type: 'Out of an item (or items)',
      value: '',
      refundAmount: null,
      refundReason: {
        label: 'Other',
        value: 'Other',
      },
      refundNote: null,
      approvalCode: '',
      refund_application_fee:
        refundData.refundApplicationFee === true ||
        refundData.refundApplicationFee === false
          ? refundData.refundApplicationFee
          : false,
      reverse_transfer:
        refundData.reverseTransfer === true ||
        refundData.reverseTransfer === false
          ? refundData.reverseTransfer
          : true,
      previousRefund: refundData.totalRefund,
      lastRefund: refundData.lastRefund,
      refundSummary: refundData.refundSummary,
      hasAlreadyRefunded: refundData.hasAlreadyRefunded,
    };
  },

  validationSchema: yup.object().shape({
    value: yup
      .string()
      .required('Description is required!')
      .min(5, 'Please enter at least 5 characters')
      .max(144, 'Description cannot be greater than 144 characters'),
  }),

  handleSubmit: async (values, { props, setSubmitting }) => {
    const refundAmountSchema = yup.object({
      refundAmount: yup
        .number()
        .nullable()
        .required('Refund Amount is required!')
        .positive('Refund Amount must be positive!'),
    });
    try {
      refundAmountSchema.validateSync(
        { refundAmount: values.refundAmount },
        { abortEarly: false },
      );
    } catch (error) {
      toast.error(error.errors[0]);
      setSubmitting(false);
      return;
    }

    const refundNoteSchema = yup.object({
      refundNote: yup
        .string()
        .nullable('Refund Note is required!')
        .min(10, 'Please enter at least 10 characters')
        .max(8000, 'Please enter less than 8000 characters')
        .required('Refund Note is required!'),
    });
    try {
      refundNoteSchema.validateSync(
        { refundNote: values.refundNote },
        { abortEarly: false },
      );
    } catch (error) {
      toast.error(error.errors[0]);
      setSubmitting(false);
      return;
    }

    const approvalCodeSchema = yup.object({
      approvalCode: yup.string().required('Please enter your approval code!'),
    });
    try {
      approvalCodeSchema.validateSync(
        { approvalCode: values.approvalCode },
        { abortEarly: false },
      );
    } catch (error) {
      toast.error(error.errors[0]);
      setSubmitting(false);
      return;
    }

    const isConfirm = await swal({
      title: 'Are you sure?',
      text:
        'Are you sure you absolutely cannot prepare the order. If you still would like to cancel this order, please confirm below or cancel to go back',
      buttons: ['Cancel', 'Confirm'],
    });

    if (!isConfirm) {
      setSubmitting(false);
      return;
    }

    const refundInput = {
      user_role: 'kravein_super_admin',
      user_id: props.userId,
      menu_order_id: props.order.menu_order_id,
      action: 'refund',
      approval_code: values.approvalCode,
      refund_application_fee: values.refund_application_fee,
      reverse_transfer: values.reverse_transfer,
      refunds: {
        type: values.refundReason.value,
        value: values.refundAmount,
        note: { type: values.refundNote },
      },
    };

    try {
      const res = await updateOrderWithRefund({ input: refundInput });
      if (res) {
        toast.error(res);
        setSubmitting(false);
        return;
      }
    } catch (error) {
      console.log(error);
      setSubmitting(false);
      toast.error('Failed to process refund');
      return;
    }

    const note = {
      type: values.type,
      value: values.value,
      date: moment().toISOString(),
    };

    if (props.order.status === 'RECEIVED') {
      props.handleCancelOrder(
        props.order.menu_order_id,
        'cancelreceived',
        note,
      );
    }
    if (props.order.status === 'CONFIRMED') {
      props.handleCancelOrder(
        props.order.menu_order_id,
        'cancelconfirmed',
        note,
      );
    }
    if (props.order.status === 'READY') {
      props.handleCancelOrder(props.order.menu_order_id, 'cancelready', note);
    }

    setSubmitting(false);
    props.onClose();
  },

  displayName: 'CancelAndRefundForm',
})(Form);

export default CancelAndRefundForm;
